import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormLabel,
  FormControl,
  Link,
  TextField,
  Typography,
  Card,
  AppBar,
  Toolbar,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useNavigate, useLocation, Outlet } from "react-router-dom";

import ConfirmDialog from "../components/ConfirmDialog";
import InformationDialog from "../components/InformationDialog";
import userApi from "../api/userApi";

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const navigate = useNavigate();

  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    title: "",
    content: "",
    onConfirm: () => {},
    onClose: () => {},
  });
  const [informationDialogState, setInformationDialogState] = useState({
    open: false,
    title: "",
    content: "",
    confirmText: "",
    onConfirm: () => {},
  });

  // slider giống trang login
  const slides = [
    "./Xuathoadon01.jpg",
    "./Xuathoadon02.jpg",
    "./Xuathoadon03.jpg",
    "./Xuathoadon04.jpg",
    "./Xuathoadon05.jpg",
  ];
  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setSlideIdx((i) => (i + 1) % slides.length);
    }, 4000);
    return () => clearInterval(t);
  }, [slides.length]);

  const prev = () =>
    setSlideIdx((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setSlideIdx((i) => (i + 1) % slides.length);

  const validate = () => {
    // regex email đơn giản
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!email) {
      setEmailError(true);
      setEmailErrorMessage("Email không được để trống");
      return false;
    }
    if (!ok) {
      setEmailError(true);
      setEmailErrorMessage("Email không hợp lệ");
      return false;
    }
    setEmailError(false);
    setEmailErrorMessage("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const response = await userApi.SendEmailChangePasswordWithEmail(email);

      if (response.success) {
        setInformationDialogState({
          open: true,
          title: "",
          content:
            "Chúng tôi đã gửi liên kết để đặt lại mật khẩu đến email của bạn. Vui lòng kiểm tra email.",
          onConfirm: () => {
            setInformationDialogState({
              ...confirmDialogState,
              open: false,
            });
            navigate("/login");
          },
          confirmText: "Đóng",
        });
      }
    } catch (err) {
      toast.error(err.message || "Có lỗi xảy ra.");
      console.error(err);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Header giữ nguyên */}
      <AppBar
        position="static"
        component="div"
        sx={{
          backgroundColor: "transparent",
          boxShadow: "none",
          "& .MuiToolbar-root": { color: "text.primary" },
          "& .MuiButton-root": { color: "inherit" },
          position: "absolute",
          top: 0,
          right: 0,
          left: 0,
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 6 } }}>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}
          >
            <img
              src="Relisoft1024_ngang.svg"
              alt="Logo"
              style={{ height: "40px", marginRight: "10px" }}
            />
          </Typography>

          {/* có thể thêm menu như trang login nếu cần */}
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}
          >
            <img
              src="Relisoft1024_ngang.svg"
              alt="Logo"
              style={{ height: "30px", marginRight: "10px" }}
            />
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Body: trái slider / phải form */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          marginTop: "64px",
          paddingLeft: "32px",
          paddingRight: "32px",
        }}
      >
        {/* LEFT: slider */}
        <Box
          sx={{
            position: "relative",
            flex: { xs: "0 0 auto", md: 1 },
            height: { xs: 240, md: "90vh" },
            overflow: "hidden",
          }}
        >
          {slides.map((src, i) => (
            <Box
              key={src}
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${src})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                transition: "opacity 600ms ease",
                opacity: i === slideIdx ? 1 : 0,
              }}
            />
          ))}

          <IconButton
            aria-label="prev"
            onClick={prev}
            sx={{
              position: "absolute",
              top: "50%",
              left: 12,
              transform: "translateY(-50%)",
              bgcolor: "rgba(0,0,0,0.35)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="next"
            onClick={next}
            sx={{
              position: "absolute",
              top: "50%",
              right: 12,
              transform: "translateY(-50%)",
              bgcolor: "rgba(0,0,0,0.35)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>

          {/* Dots */}
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: 1,
            }}
          >
            {slides.map((_, i) => (
              <Box
                key={i}
                onClick={() => setSlideIdx(i)}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor:
                    i === slideIdx ? "common.white" : "rgba(255,255,255,0.6)",
                  cursor: "pointer",
                }}
              />
            ))}
          </Box>
        </Box>

        {/* RIGHT: form quên mật khẩu */}
        <Box
          sx={{
            flex: { xs: "0 0 auto", md: 1 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2, md: 6 },
            py: { xs: 3, md: 0 },
          }}
        >
          <Card
            variant="outlined"
            sx={{
              width: "100%",
              maxWidth: 440,
              p: { xs: 3, md: 4 },
              boxShadow: { md: 3 },
              borderRadius: 3,
            }}
          >
            <Typography
              component="h1"
              variant="h4"
              sx={{
                width: "100%",
                fontSize: "clamp(2rem, 4vw, 2.15rem)",
                mb: 2,
              }}
            >
              Quên mật khẩu
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <FormControl>
                <FormLabel htmlFor="email">Email</FormLabel>
                <TextField
                  error={emailError}
                  helperText={emailErrorMessage}
                  id="email"
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                  fullWidth
                  variant="outlined"
                  color={emailError ? "error" : "primary"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormControl>

              {/* Nút xác nhận: canh giữa */}
              <Button
                type="submit"
                variant="contained"
                onClick={validate}
                sx={{
                  alignSelf: "center",
                  width: 200,
                  mt: 1,
                }}
              >
                Xác nhận
              </Button>

              {/* Link quay lại đăng nhập */}
              <Box sx={{ width: "100%", textAlign: "center", mt: 1 }}>
                <Link href="/login" underline="hover">
                  Quay lại đăng nhập
                </Link>
              </Box>
            </Box>
          </Card>
        </Box>
      </Box>
      <ConfirmDialog
        open={confirmDialogState.open}
        title={confirmDialogState.title}
        content={confirmDialogState.content}
        onConfirm={confirmDialogState.onConfirm}
        onClose={confirmDialogState.onClose}
      />
      <InformationDialog
        open={informationDialogState.open}
        title={informationDialogState.title}
        content={informationDialogState.content}
        onConfirm={informationDialogState.onConfirm}
        confirmText={informationDialogState.confirmText}
      />
    </Box>
  );
}

export default ForgotPasswordPage;
